/**
 * Swipe-to-action for entry rows (mirrors listto's row swipe).
 *
 * Right swipe toggles favorite; left swipe asks to delete (deletion is
 * destructive, so it opens a confirm rather than committing blind). The physics
 * — deadzone, axis-lock, rubber-band, rising-edge haptic — come from the shared
 * recognizer, so this matches the calendar/day swipes. A pointer that begins on
 * a button or link is ignored so the existing tap targets keep working, and the
 * card keeps its keyboard/click affordances intact.
 */
import { createSignal } from "solid-js";

import { closestSelector } from "@/lib/gestures/guards";
import { applyRubberBand, createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";
import { ENTRY_ROW_SWIPE } from "@/lib/gestures/presets";

interface EntrySwipeOptions {
  onToggleFavorite: () => void;
  onRequestDelete: () => void;
  getSurfaceRef: () => HTMLElement | undefined;
}

const {
  thresholdPx: THRESHOLD_PX,
  commitThresholdPx: COMMIT_THRESHOLD_PX,
  rubberBandLimitPx: RUBBER_BAND_LIMIT,
  rubberBandFactor: RUBBER_BAND_FACTOR,
  hapticMs: HAPTIC_MS,
  commitAnimationMs: COMMIT_ANIMATION_MS,
  cancelAnimationMs: CANCEL_ANIMATION_MS,
  fallbackWidthPx: FALLBACK_WIDTH_PX,
} = ENTRY_ROW_SWIPE;

export function useEntrySwipeGestures(options: EntrySwipeOptions) {
  const [tx, setTx] = createSignal(0);
  const [isSwiping, setIsSwiping] = createSignal(false);
  const [isAnimating, setIsAnimating] = createSignal(false);

  let hasSwiped = false;

  const settle = (ms: number) => {
    setTx(0);
    globalThis.setTimeout(() => {
      setIsAnimating(false);
    }, ms);
  };

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: THRESHOLD_PX,
    commitPx: COMMIT_THRESHOLD_PX,
    haptics: true,
    hapticMs: HAPTIC_MS,
    canStart: ({ target }) =>
      // Ignore drags that begin on the action buttons or editable fields. Links
      // (title/body) are allowed to start a swipe; their click is suppressed via
      // getHasSwiped() so a swipe never doubles as navigation.
      !closestSelector(target, "button, textarea, input, [contenteditable]"),
    onStart: () => {
      setIsSwiping(true);
      setIsAnimating(false);
    },
    onMove: (sample) => {
      setTx(applyRubberBand(sample.dx, RUBBER_BAND_LIMIT, RUBBER_BAND_FACTOR));
    },
    onEnd: () => {
      hasSwiped = true;
      setIsSwiping(false);
      setIsAnimating(true);

      const finalTx = tx();
      const width = options.getSurfaceRef()?.clientWidth ?? FALLBACK_WIDTH_PX;

      if (finalTx > COMMIT_THRESHOLD_PX) {
        // Swipe right → toggle favorite, then bounce back.
        options.onToggleFavorite();
        settle(COMMIT_ANIMATION_MS);
      } else if (finalTx < -COMMIT_THRESHOLD_PX) {
        // Swipe left → slide out and ask to delete.
        setTx(-width);
        globalThis.setTimeout(() => {
          options.onRequestDelete();
          setTx(0);
          setIsAnimating(false);
        }, COMMIT_ANIMATION_MS);
      } else {
        settle(CANCEL_ANIMATION_MS);
      }
    },
    onCancel: () => {
      setIsSwiping(false);
      setIsAnimating(true);
      settle(CANCEL_ANIMATION_MS);
    },
  });

  const base = pointerHandlers(recognizer);

  return {
    tx,
    isSwiping,
    isAnimating,
    handlers: base,
    getHasSwiped: () => hasSwiped,
    resetHasSwiped: () => {
      hasSwiped = false;
    },
  };
}
